// Yolutap brand mark — matches Brand Handoff v2.0
// Renders the puzzle-piece icon + wordmark with the amber dot over the "o".
import type { CSSProperties } from "react";

type Variant = "full" | "icon" | "wordmark";

interface Props {
  variant?: Variant;
  height?: number;
  onDark?: boolean;
  className?: string;
  style?: CSSProperties;
}

const TEAL_INK = "#143F4B";
const PAPER = "#FBFAF7";
const AMBER = "#F0A63A";

export function YolutapIcon({
  size = 40,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <g transform="translate(20, 20) rotate(-6, 50, 50)">
        <path
          fill="#1E5A6B"
          d="M0 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L65 100 C65 118 35 118 35 100 L0 100 Z"
        />
      </g>
      <g transform="translate(125, 14) rotate(8, 50, 50)">
        <path
          fill="#F0A63A"
          d="M0 0 L100 0 L100 100 L65 100 C65 118 35 118 35 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"
        />
      </g>
      <g transform="translate(14, 130) rotate(-3, 50, 50)">
        <path
          fill="#4BAE8C"
          d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L0 100 Z"
        />
      </g>
      <g transform="translate(130, 132) rotate(6, 50, 50)">
        <path
          fill="#7A5AAE"
          d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"
        />
      </g>
    </svg>
  );
}

export function YolutapWordmark({
  fontSize = 40,
  onDark = false,
  bgColor,
  className,
}: {
  fontSize?: number | string;
  onDark?: boolean;
  bgColor?: string;
  className?: string;
}) {
  const color = onDark ? PAPER : `var(--color-teal-ink, ${TEAL_INK})`;
  const ringColor = bgColor ?? (onDark ? TEAL_INK : PAPER);
  return (
    <span
      className={className}
      style={{
        fontFamily: "'Nunito Sans', system-ui, sans-serif",
        fontWeight: 700,
        letterSpacing: "-0.025em",
        color,
        display: "inline-flex",
        alignItems: "baseline",
        whiteSpace: "nowrap",
        lineHeight: 0.85,
        fontSize,
      }}
    >
      Y
      <span style={{ position: "relative", display: "inline-block" }}>
        o
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            width: "0.18em",
            height: "0.18em",
            borderRadius: "50%",
            background: AMBER,
            top: "0.06em",
            right: "-0.02em",
            boxShadow: `0 0 0 0.045em ${ringColor}`,
          }}
        />
      </span>
      lutap
    </span>
  );
}

// Высота заглавных Nunito Sans — доля кегля (capHeight 705 / unitsPerEm 1000).
const CAP = 0.705;
const LOCKUP_ICON = "max(24px, 1.4em)";

/**
 * Primary-локап для шапок (Brand Handoff v2.0, §01–02): иконка + вордмарк.
 * Кегль вордмарка — font-size родителя, поэтому размеры по брейкпоинтам
 * задаёт CSS шапки. Иконка не меньше 24px; отступ до вордмарка — 0.42×
 * высоты заглавных; центр иконки — посередине между базовой линией и линией
 * заглавных. bgColor — фон под шапкой: им рисуется кольцо вокруг точки.
 */
export function YolutapLockup({ bgColor = `var(--color-paper, ${PAPER})` }: { bgColor?: string }) {
  return (
    <span className="yt-lockup" style={{ display: "inline-block", whiteSpace: "nowrap", lineHeight: 1 }}>
      <YolutapIcon
        className="yt-lockup__icon"
        style={{
          display: "inline-block",
          width: LOCKUP_ICON,
          height: LOCKUP_ICON,
          verticalAlign: `calc(${CAP / 2}em - ${LOCKUP_ICON} / 2)`,
          marginRight: `${(0.42 * CAP).toFixed(3)}em`,
        }}
      />
      <YolutapWordmark className="yt-lockup__wm" fontSize="1em" bgColor={bgColor} />
    </span>
  );
}

export function YolutapLogo({
  variant = "full",
  height = 40,
  onDark = false,
  className,
  style,
}: Props) {
  if (variant === "icon") {
    return <YolutapIcon size={height} className={className} />;
  }
  if (variant === "wordmark") {
    return <YolutapWordmark fontSize={height} onDark={onDark} className={className} />;
  }
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: height * 0.3, ...style }}
    >
      <YolutapIcon size={height} />
      <YolutapWordmark fontSize={height * 0.7} onDark={onDark} />
    </span>
  );
}
